import { useQuery } from '@tanstack/react-query';
import { getActivePartnerUserCount } from '../services/PartnerService';

// Central hook for the two "there is only one of them on this partner"
// derivations that drive the hide-teams UX and the team-reveal moment.
// One query, both counts:
//   - hasSoloUser: total active users === 1. Drives the hide of the whole
//     teams UI and every team-adjacent affordance, because there is no
//     meaningful team routing to display until a second user joins.
//   - hasSoloWalletHolder: active wallet-holders === 1. Drives the reveal
//     modal that fires only on the 1-to-2 wallet-holder crossing, so a
//     solo super admin adding their first wallet-holding teammate gets a
//     one-time naming moment for the default team.
// Consumers should treat isLoading as "assume the safer, more visible
// state" (both booleans false) so no surface flashes hidden while the
// counts are in flight.
export function usePartnerHasSoloUser(partnerId) {
    const { data, isLoading, error } = useQuery({
        queryKey: ['partnerHasSoloUser', partnerId],
        queryFn: () => getActivePartnerUserCount(partnerId).then(res => res.data),
        enabled: !!partnerId,
        // Zero staleTime so the derived isSolo state refreshes whenever a
        // consumer mounts. User-status changes (activations, suspensions,
        // deletions) happen from many code paths, and not every mutation
        // path invalidates this key. Refetching on mount is cheap for a
        // single integer count, and it keeps solo-state UI in sync without
        // threading invalidation through every user-mutation flow.
        staleTime: 0,
        // Never retry a 4xx: an auth or permission refusal will not
        // resolve by retrying, and each retry produces another visible
        // error in the console. Retry on 5xx (transient) up to twice.
        retry: (failureCount, err) => {
            const status = err?.response?.status;
            if (status && status >= 400 && status < 500) return false;
            return failureCount < 2;
        },
    });

    return {
        hasSoloUser: data?.countActiveUsers === 1,
        hasSoloWalletHolder: data?.countActiveWalletHolders === 1,
        isLoading,
        error,
    };
}
